import * as React from 'react';
import { Column, Columns, div, Input, Loading } from 'lqx-react';
import { ErrorHighligter } from '../../shared';
import { PolicyDocuments } from './policy-documents';
import { Policy } from '../../api/policy';
import { PolicyLabel } from './shared';
import { useCollection, useIO } from '@type-r/react';
import { Company } from '../../api/company';
import { styled } from '@material-ui/core';
import { CoveredAssetTypes, PolicyTypes, SyndicationRuleTypes } from '../../api/reference-data';
import { Participant } from '../../api/participant';
import { useParticipants } from '../../hooks';
import { DigiSelect } from '../../components/select';

export const PolicyDetailsForm = ({ policy, validate }: { policy: Policy, validate }) => {
    const companies = useCollection.of(Company);
    const participants = useCollection.of(Participant);
    const insuredParties = useParticipants(companies, participants, ["InsuredParty"]);
    const brokers = useParticipants(companies, participants, ["Broker"]);

    const isReady = useIO(async () => await companies.fetch() && await participants.fetch());

    return !isReady ? <Loading /> : (<>
        <PolicyInfo>
            Confirm out any missing fields to get your policy started. Once you upload your documents, the policy will begin the digitization process.
        </PolicyInfo>
        <Columns>
            <Column><PolicyLabel>Insured Party Name *</PolicyLabel></Column>
            <Column><PolicyLabel>Insurance Carrier Name *</PolicyLabel></Column>
            <Column><PolicyLabel>Broker *</PolicyLabel></Column>
            <Column><PolicyLabel>Platform Broker *</PolicyLabel></Column>
        </Columns>
        <Columns>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'insuredParty' ] }>
                    <ParticipantSelect { ...{ onBlur: validate } } data-qa-id="insured party name" id="companyId" label="name" $value={ policy.$.insuredParty } options={ insuredParties } />
                </ErrorHighligter>
            </Column>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'insuranceCarrierName' ] }>
                    <Input data-qa-id="insurance carrier name" onBlur={ validate } readOnly={ true } $value={ policy.policyInsuranceCarriers.models[ 0 ].$.name } />
                </ErrorHighligter>
            </Column>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'broker' ] }>
                    <ParticipantSelect { ...{ onBlur: validate } } data-qa-id="broker" id="companyId" label="name" $value={ policy.$.broker } options={ brokers } />
                </ErrorHighligter>
            </Column>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'platformBroker' ] }>
                    <Input data-qa-id="platform broker" onBlur={ validate } readOnly $value={ policy.platformBroker.$.name } />
                </ErrorHighligter>
            </Column>
        </Columns>
        <Columns>
            <Column><PolicyLabel>Policy Name *</PolicyLabel></Column>
            <Column><PolicyLabel>Carrier Policy #</PolicyLabel></Column>
            <Column><PolicyLabel>Policy Inception Date *</PolicyLabel></Column>
            <Column><PolicyLabel>Policy Expiration Date *</PolicyLabel></Column>
        </Columns>
        <Columns>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'name' ] }>
                    <Input data-qa-id="policy name" onBlur={ validate } $value={ policy.$.name } />
                </ErrorHighligter>
            </Column>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'carrierPolicyNumber' ] }>
                    <Input data-qa-id="carrier policy" onBlur={ validate } $value={ policy.$.carrierPolicyNumber } />
                </ErrorHighligter>
            </Column>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'inceptionDate' ] }>
                    <Input data-qa-id="policy inception date" { ...{ onCalendarClose: validate } } date $value={ policy.$.inceptionDate } />
                </ErrorHighligter>
            </Column>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'expiryDate' ] }>
                    <Input data-qa-id="policy expiration date" { ...{ onCalendarClose: validate } } date $value={ policy.$.expiryDate } />
                </ErrorHighligter>
            </Column>
        </Columns>
        <Columns>
            <Column><PolicyLabel>Policy Type *</PolicyLabel></Column>
            <Column><PolicyLabel>Asset Type *</PolicyLabel></Column>
            <Column><PolicyLabel>Commission Rate *</PolicyLabel></Column>
            <Column><PolicyLabel>Syndication Rules</PolicyLabel></Column>
        </Columns>
        <Columns>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'policyType' ] }>
                    <DigiSelect { ...{ onBlur: validate } } data-qa-id="policy type" id="id" label="name" $value={ policy.$.policyType } options={ PolicyTypes } />
                </ErrorHighligter>
            </Column>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'coveredAssetType' ] }>
                    <DigiSelect { ...{ onBlur: validate } } data-qa-id="asset type" id="id" label="name" $value={ policy.$.coveredAssetType } options={ CoveredAssetTypes } />
                </ErrorHighligter>
            </Column>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'policyCommissionRate' ] }>
                    <Input data-qa-id="commission rate" onBlur={ validate } $value={ policy.$.policyCommissionRate } />
                </ErrorHighligter>
            </Column>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'syndicationRuleType' ] }>
                    <DigiSelect { ...{ onBlur: validate } } data-qa-id="syndication rules" id="id" label="name" $value={ policy.$.syndicationRuleType } options={ SyndicationRuleTypes } />
                </ErrorHighligter>
            </Column>
        </Columns>
        <Columns>
            <Column><PolicyLabel>Additional Name Insured</PolicyLabel></Column>
            <Column><PolicyLabel>Loss Payee</PolicyLabel></Column>
            <Column><PolicyLabel></PolicyLabel></Column>
            <Column><PolicyLabel></PolicyLabel></Column>
        </Columns>
        <Columns>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'additionalInsuredName' ] }>
                    <Input data-qa-id="additional name insured" onBlur={ validate } $value={ policy.additionalInsuredName.$.name } />
                </ErrorHighligter>
            </Column>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'lossPayee' ] }>
                    <Input data-qa-id="loss payee" onBlur={ validate } $value={ policy.lossPayee.$.name } />
                </ErrorHighligter>
            </Column>
            <Column></Column>
            <Column></Column>
        </Columns>
        <PolicyTitle>Additional Policy Documents</PolicyTitle>
        <PolicyInfo>
            Add any endorsements, modules, rider addendums and customer specific policy documents,
        </PolicyInfo>
        <PolicyDocuments policy={ policy } />
    </>)
};

const ParticipantSelect = styled(DigiSelect)({
    width: '50px'
});

const PolicyInfo = div({
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '15px',
    color: 'LightGray',
    paddingTop: '10px',
    paddingLeft: '20px',
    paddingBottom: '20px',
});

export const PolicyTitle = div({
    marginLeft: 15,
    marginTop: 15,
    color: '#3d6889',
    fontFamily: 'Montserrat',
    fontSize: 16,
    fontWeight: 600,
    overflow: 'hidden',
    whiteSpace: 'nowrap'
});

